import { API_ERROR_MSG } from '@share/constant';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { SocialBox, SocialBoxContainer } from '@style/emotion';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { toast } from 'react-toastify';
import { useAPI, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import ActivityList from '@com/profile/ActivityList';
import CollectionList from '@com/profile/CollectionList';
import Footer from '@com/footer/Footer';
import Header from '@com/header/Header';
import MouseOver from '@com/utils/MouseOver';
import ProductList from '@com/profile/ProductList';
import ShareBtn from '@com/buttons/ShareBtn';
import SkeletonImage from '@com/utils/SkeletonImage';
import TopSection from '@com/TopSection';
import styled from '@emotion/styled';

const UserContainer = styled.div`
  min-height: 320px;
`;

const AvatarContainer = styled.div`
  width: min(256px, 100%);
  div {
    width: min(256px, 100%);
  }
`;

const SideBtn = styled.div`
  width: 6rem;
  text-align: center;
`;

const UserName = styled.h1`
  line-height: 64px;
`;

function Profile(): JSX.Element {
  /* Define state / hook */
  const { chainId } = web3.useContainer();
  const { userData } = user.useContainer();
  const [searchParams] = useSearchParams();
  const { t, ready } = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getProfile } = useAPI();
  const [fallbackAvatar, setFallbackAvatar] = useState<string>();
  const { displayName, getUserIdentifier, showDescription, copyText } =
    useHelper();
  const { data, isSuccess, isError } = useQuery(
    [getProfile.name, chainId, uuid],
    () => getProfile(chainId, uuid),
    {
      retry: 0,
      onError() {
        // If we cannot get user record, change loading skeleton to dead image
        setFallbackAvatar('');
      },
    }
  );
  const { isLogin } = user.useContainer();

  const tabIndexMap: Record<string, number> = {
    owned: 0,
    on_sales: 1,
    created: 2,
    activity: 3,
  };

  const PARAM_NAME = 'category';
  const selectedIndex = useMemo(() => {
    return tabIndexMap[String(searchParams.get(PARAM_NAME))] || 0;
  }, [location.search]);

  const isMyProfile =
    (data?.uuid != null && userData?.user?.uuid === data?.uuid) ||
    uuid === 'my';

  /* Styling */
  useEffect(() => {
    if (isMyProfile) {
      // must be equal false cause when refreshing isLogin == null
      if (isLogin === false && ready) {
        // Prevent showing 401 error message
        toast.info(t('alertMessage.login'), { toastId: API_ERROR_MSG });
        navigate(`/`);
      }
    }
  }, [isLogin, isMyProfile, ready]);

  /* Custom Data Object for render or other usages */

  /* Render control function / logic */
  const copyAddress = useDebouncedCallback(
    () => {
      copyText(
        data?.wallet?.[0].wallet_address,
        t('toastMessage.addressCopied')
      );
    },
    1000,
    { leading: true, trailing: false }
  );

  /* Styling */

  /* Render */
  return (
    <>
      <Header />
      <TopSection />
      <section className="authors-2 content-container tf-section">
        <div className="themesflat-container">
          <div className="flat-tabs tab-authors">
            <UserContainer className="author-profile flex">
              <AvatarContainer className="d-flex align-items-center">
                <SkeletonImage
                  alt="Avatar Image"
                  ratio={1}
                  size={[512]}
                  src={data?.avatar || fallbackAvatar}
                />
              </AvatarContainer>
              <div className="infor-profile">
                <UserName className="title text-truncate">
                  {isError
                    ? t('profile.userNotFound')
                    : displayName(data?.name)}
                </UserName>
                <p className="h3 mb-5 title">{showDescription(data?.bio)}</p>
                <h3 className="title">
                  {t('profile.totalNFT')}
                  <span className="ml-2">{data?.total_nft || 0}</span>
                </h3>
                <div className="row-12">
                  <form className="copy mt-5">
                    <div className="position-absolute">
                      {data?.wallet?.[0]?.is_treasury ? (
                        <MouseOver text={t('itemDetails.custody')}>
                          <i
                            className="fa fa-shield mr-1 fs-16 p-3"
                            style={{ color: 'var(--primary-color3)' }}
                          />
                        </MouseOver>
                      ) : (
                        <i
                          className="fa fa-wallet mr-1 fs-16 p-3"
                          style={{ color: 'var(--primary-color3)' }}
                        />
                      )}
                    </div>
                    <input
                      readOnly
                      defaultValue={data?.wallet?.[0].wallet_address}
                      type="text"
                    />
                    <button
                      className="btn-copycode"
                      data-test="profile-copy-addr-btn"
                      type="button"
                      onClick={copyAddress}
                    >
                      <div className="d-inline-flex">
                        <div className="d-flex align-items-center">
                          <i className="icon-fl-file-1" />
                        </div>
                      </div>
                    </button>
                  </form>
                </div>
              </div>
              {/* social media */}
              <SocialBoxContainer className="widget-social style-3 d-flex justify-content-end mr-md-4 mr-sm-0">
                <SocialBox className="mr-4">
                  {data?.social_links
                    .filter((link) => link.url)
                    .map((item) => (
                      <li key={item.name}>
                        <a href={item.url} rel="noreferrer" target="_blank">
                          <i className={`${item.icon}`} />
                        </a>
                      </li>
                    ))}
                  {isMyProfile && (
                    <li>
                      <ShareBtn
                        className="ml-auto"
                        dataTest="profile-share-btn"
                        path={`/profile/${getUserIdentifier(userData?.user)}`}
                      />
                    </li>
                  )}
                </SocialBox>
              </SocialBoxContainer>
              {isMyProfile && (
                <div className="btn-profile pd-t-20 d-flex flex-column">
                  <Link
                    className="sc-button style-1"
                    data-test="setting-link"
                    to="/edit-profile"
                  >
                    <SideBtn>{t('profile.setting')}</SideBtn>
                  </Link>
                  <Link
                    className="sc-button style-1 mt-4"
                    data-test="receipt-link"
                    to="/receipt"
                  >
                    <SideBtn>{t('profile.receipts')}</SideBtn>
                  </Link>
                  <Link
                    className="sc-button style-1 mt-4"
                    data-test="balance-link"
                    to="/balance"
                  >
                    <SideBtn>{t('profile.accountStatement')}</SideBtn>
                  </Link>
                </div>
              )}
            </UserContainer>
            <Tabs
              selectedIndex={selectedIndex}
              onSelect={(index) => {
                navigate(
                  `${location.pathname}?${PARAM_NAME}=${
                    Object.keys(tabIndexMap)[index]
                  }`
                );
              }}
            >
              <TabList>
                <Tab>
                  <i aria-hidden className="fa fa-image mr-3" />
                  {t('profile.owned')}
                </Tab>
                <Tab>
                  <i aria-hidden className="fa fa-image mr-3" />
                  {t('profile.onSale')}
                </Tab>
                <Tab>
                  <i aria-hidden className="fa fa-image mr-3" />
                  {t('profile.created')}
                </Tab>
                <Tab>
                  <i aria-hidden className="fa fa-clipboard mr-3" />
                  {t('profile.activity')}
                </Tab>
              </TabList>

              <div className="content-tab min-h-50">
                <TabPanel>
                  <CollectionList isRender={isSuccess} isStop={isError} />
                  <div className="min-h-50 w-100">
                    <ProductList
                      category="owned"
                      isMyProfile={isMyProfile}
                      isRender={isSuccess}
                      isStop={isError}
                      sort="latest"
                    />
                  </div>
                </TabPanel>

                <TabPanel>
                  <ProductList
                    category="on_sales"
                    isRender={isSuccess}
                    isStop={isError}
                    sort="latest"
                  />
                </TabPanel>

                <TabPanel>
                  <ProductList
                    category="created"
                    isRender={isSuccess}
                    isStop={isError}
                    sort="latest"
                  />
                </TabPanel>

                <TabPanel>
                  <ActivityList isRender={isSuccess} isStop={isError} />
                </TabPanel>
              </div>
            </Tabs>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Profile;
